/** Props for {@link Thumbnail}. */
export interface ThumbnailProps {
  /** Image URL. When absent, a labelled placeholder is rendered instead. */
  readonly src?: string | undefined;
  /**
   * Alternative text.
   *
   * Required rather than optional: a vehicle photo carries real information, and an unlabelled
   * image is invisible to anyone using a screen reader.
   */
  readonly alt: string;
  /** Rendered size in pixels. */
  readonly size?: number;
  /** Text shown when there is no image. */
  readonly placeholder?: string;
}

/**
 * A fixed-size image tile with a placeholder for vehicles that have no photo yet.
 *
 * @param props - Component props.
 * @returns The thumbnail element.
 */
export function Thumbnail({
  src,
  alt,
  size = 96,
  placeholder = 'No photo',
}: ThumbnailProps): React.JSX.Element {
  const dimensions = { width: `${size}px`, height: `${size}px` };

  if (src === undefined || src === '') {
    return (
      <div
        className="gm-thumb gm-thumb--placeholder"
        style={dimensions}
        role="img"
        aria-label={`${alt}: ${placeholder}`}
      >
        {placeholder}
      </div>
    );
  }

  return (
    <img className="gm-thumb" style={dimensions} src={src} alt={alt} loading="lazy" width={size} height={size} />
  );
}
